import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  InputAdornment,
  IconButton,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  AWS_PARTNER_SUPPORT_PLAN_PRESETS,
  CHARGE_SELECTION_GREATER,
  buildAwsPartnerSupportDefaultParameters,
  formatMinimumSpendReadable,
  formatPartnerSupportTierLinesAwsStyle,
} from "./awsPartnerSupportBilling";

function mergeParams(prev, patch) {
  return { ...(prev || {}), ...patch };
}

function normalizeTiersList(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((t) => ({
    from_amount: t.from_amount,
    to_amount: t.to_amount === undefined ? null : t.to_amount,
    percent: t.percent,
  }));
}

function planOptionsFromMeta(requiredParameters) {
  const planParam = (requiredParameters || []).find((p) => p.key === "plan_name");
  if (planParam?.options?.length) {
    return planParam.options.map((o) => ({ value: o.value, label: o.label || o.value }));
  }
  return Object.keys(AWS_PARTNER_SUPPORT_PLAN_PRESETS).map((value) => ({
    value,
    label:
      value === "BUSINESS_SUPPORT"
        ? "Business Support"
        : value === "ENTERPRISE_SUPPORT"
          ? "Enterprise Support"
          : value === "UNIFIED_OPERATIONS"
            ? "Unified Operations"
            : value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
  }));
}

function chargeSelectionOptionsFromMeta(requiredParameters) {
  const p = (requiredParameters || []).find((x) => x.key === "charge_selection");
  if (p?.options?.length) return p.options.map((o) => ({ value: o.value, label: o.label || o.value }));
  return [{ value: CHARGE_SELECTION_GREATER, label: "Greater of minimum or tiered" }];
}

/** Vertical form for edit modal: plan dropdown + minimum, charge, usage bands (one plan in `parameters`). */
function AwsPartnerSupportSinglePlanFields({ disabled, parameters, onChange, requiredParameters }) {
  const params = parameters && typeof parameters === "object" ? parameters : {};
  const planOpts = useMemo(() => planOptionsFromMeta(requiredParameters), [requiredParameters]);
  const chargeOpts = useMemo(() => chargeSelectionOptionsFromMeta(requiredParameters), [requiredParameters]);
  const planName = String(params.plan_name || "BUSINESS_SUPPORT").toUpperCase();
  const validPlan = AWS_PARTNER_SUPPORT_PLAN_PRESETS[planName] ? planName : "BUSINESS_SUPPORT";
  const preset = AWS_PARTNER_SUPPORT_PLAN_PRESETS[validPlan];
  const tiers = normalizeTiersList(params.tiers);
  const mc = params.minimum_charge && typeof params.minimum_charge === "object" ? params.minimum_charge : {};
  const amountStr =
    mc.amount === "" || mc.amount === null || mc.amount === undefined ? "" : String(mc.amount);
  const unitVal = mc.unit || preset?.minimum_charge?.unit || "PER_MONTH";
  const chargeSelection = params.charge_selection || CHARGE_SELECTION_GREATER;
  const showGreaterOfBlock = chargeSelection === CHARGE_SELECTION_GREATER;
  const minimumSpendReadable = formatMinimumSpendReadable(validPlan, mc);
  const tierSummaryLines = formatPartnerSupportTierLinesAwsStyle(normalizeTiersList(params.tiers));

  const setFull = (next) => {
    if (disabled) return;
    if (onChange) onChange(next);
  };
  const applyPlanPreset = (nextPlan) => {
    setFull(mergeParams(params, buildAwsPartnerSupportDefaultParameters(nextPlan)));
  };
  const patch = (partial) => setFull(mergeParams(params, { ...partial, plan_name: validPlan }));

  const borderSubtle = "#e2e8f0";
  const surfaceMuted = "#f8fafc";
  const surfaceHeader = "#fafbfc";
  const textPrimary = "#1e293b";
  const textSecondary = "#334155";
  const textMuted = "#64748b";
  const inputRadius = 2;

  const updateTier = (index, field, rawValue) => {
    const nextTiers = tiers.map((row, i) => {
      if (i !== index) return { ...row };
      if (field === "to_amount") {
        const s = String(rawValue ?? "").trim();
        if (s === "") return { ...row, to_amount: null };
        const n = parseFloat(s.replace(/[^0-9.-]/g, ""));
        return { ...row, to_amount: Number.isFinite(n) ? n : null };
      }
      if (field === "from_amount") {
        const raw = String(rawValue).replace(/[^0-9.]/g, "");
        const parts = raw.split(".");
        const normalized = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : raw;
        return { ...row, from_amount: normalized };
      }
      if (field === "percent") {
        const raw = String(rawValue).replace(/[^0-9.]/g, "");
        const parts = raw.split(".");
        const normalized = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}` : raw;
        return { ...row, percent: normalized };
      }
      return row;
    });
    patch({ tiers: nextTiers });
  };

  const addTier = () => {
    const last = tiers[tiers.length - 1];
    const from =
      last && last.to_amount != null && last.to_amount !== "" && Number.isFinite(Number(last.to_amount))
        ? Number(last.to_amount)
        : last
          ? Number(last.from_amount) || 0
          : 0;
    patch({ tiers: [...tiers, { percent: "", from_amount: from, to_amount: null }] });
  };

  const removeTier = (index) => {
    if (tiers.length <= 1) return;
    patch({ tiers: tiers.filter((_, i) => i !== index) });
  };

  const planSelectValue = planOpts.some((o) => String(o.value).toUpperCase() === validPlan)
    ? validPlan
    : planOpts[0]?.value || validPlan;

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        borderRadius: "12px",
        border: `1px solid ${borderSubtle}`,
        overflow: "hidden",
        backgroundColor: "#ffffff",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
        ...(disabled
          ? {
              opacity: 0.7,
              filter: "grayscale(0.22)",
              pointerEvents: "none",
              transition: "opacity 0.2s ease, filter 0.2s ease",
            }
          : { transition: "opacity 0.2s ease, filter 0.2s ease" }),
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          background: `linear-gradient(180deg, ${surfaceHeader} 0%, #ffffff 100%)`,
          borderBottom: `1px solid ${borderSubtle}`,
        }}
      >
        <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: textPrimary, letterSpacing: "-0.01em" }}>
          Partner support configuration
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", color: textMuted, mt: 0.75, lineHeight: 1.6 }}>
          Select a plan, then adjust minimum charge and usage bands. Values are saved on this billing rule only.
        </Typography>
      </Box>

      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <FormControl size="small" fullWidth disabled={disabled}>
          <InputLabel id="aws-partner-edit-plan-label">Plan</InputLabel>
          <Select
            labelId="aws-partner-edit-plan-label"
            label="Plan"
            value={planSelectValue}
            onChange={(e) => applyPlanPreset(String(e.target.value).toUpperCase())}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: `${inputRadius}px`,
                fontSize: "0.875rem",
              },
            }}
          >
            {planOpts.map((o) => (
              <MenuItem key={o.value} value={String(o.value).toUpperCase()} sx={{ fontSize: "0.875rem" }}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {preset && (
          <Typography sx={{ fontSize: "0.8125rem", color: textMuted, lineHeight: 1.5 }}>
            Catalog default minimum:{" "}
            <Typography component="span" sx={{ color: textSecondary, fontWeight: 500 }}>
              {formatMinimumSpendReadable(validPlan, preset.minimum_charge)}
            </Typography>
          </Typography>
        )}

        {chargeOpts.length > 1 && (
          <FormControl size="small" fullWidth disabled={disabled}>
            <InputLabel id="aws-partner-edit-charge">Charge calculation</InputLabel>
            <Select
              labelId="aws-partner-edit-charge"
              label="Charge calculation"
              value={chargeSelection}
              onChange={(e) => patch({ charge_selection: e.target.value })}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: `${inputRadius}px`, fontSize: "0.875rem" } }}
            >
              {chargeOpts.map((o) => (
                <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.875rem" }}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: textSecondary, mb: 1 }}>
            Minimum charge
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: textSecondary, lineHeight: 1.5, mb: 1.5 }}>
            {minimumSpendReadable}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-start" }}>
            <TextField
              size="small"
              label="Amount"
              disabled={disabled}
              value={amountStr}
              onChange={(e) => {
                const raw = String(e.target.value).replace(/[^0-9.]/g, "");
                const parts = raw.split(".");
                const normalized = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}` : raw;
                patch({
                  minimum_charge: { ...mc, amount: normalized === "" ? "" : normalized, unit: unitVal },
                });
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{
                flex: "1 1 120px",
                minWidth: 120,
                "& .MuiOutlinedInput-root": { borderRadius: `${inputRadius}px`, fontSize: "0.875rem" },
              }}
            />
            <FormControl size="small" sx={{ flex: "1 1 200px", minWidth: 180 }} disabled={disabled}>
              <InputLabel id="aws-partner-edit-min-unit">Unit</InputLabel>
              <Select
                labelId="aws-partner-edit-min-unit"
                label="Unit"
                value={unitVal}
                onChange={(e) =>
                  patch({
                    minimum_charge: {
                      ...mc,
                      amount:
                        amountStr === ""
                          ? AWS_PARTNER_SUPPORT_PLAN_PRESETS[validPlan].minimum_charge.amount
                          : mc.amount,
                      unit: e.target.value,
                    },
                  })
                }
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: `${inputRadius}px`, fontSize: "0.875rem" } }}
              >
                <MenuItem value="PER_ACCOUNT_PER_MONTH">Per account / month</MenuItem>
                <MenuItem value="PER_MONTH">Per month</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: textSecondary, mb: 0.75 }}>
            {showGreaterOfBlock ? "Pricing preview" : "Tier structure"}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.25, py: 0 }}>
            {tierSummaryLines.map((line, i) => (
              <Typography
                key={i}
                component="li"
                sx={{
                  fontSize: "0.8125rem",
                  color: textMuted,
                  lineHeight: 1.65,
                  mb: i === tierSummaryLines.length - 1 ? 0 : 0.5,
                }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        </Box>

        <Box>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: textSecondary, mb: 1 }}>
            Usage bands
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: "10px",
              overflow: "hidden",
              borderColor: borderSubtle,
              boxShadow: "none",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "52px minmax(0,1fr) minmax(0,1fr) 44px",
                gap: 1,
                alignItems: "center",
                px: 1.5,
                py: 1,
                backgroundColor: surfaceMuted,
                borderBottom: `1px solid ${borderSubtle}`,
              }}
            >
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: textMuted }}>%</Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: textMuted }}>
                From ($)
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: textMuted }}>
                To ($)
              </Typography>
              <Box aria-hidden />
            </Box>
            {tiers.map((row, index) => (
              <Box
                key={index}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "52px minmax(0,1fr) minmax(0,1fr) 44px",
                  gap: 1,
                  alignItems: "center",
                  px: 1.5,
                  py: 1,
                  borderTop: index === 0 ? "none" : `1px solid ${borderSubtle}`,
                  backgroundColor: "#fff",
                }}
              >
                <TextField
                  size="small"
                  disabled={disabled}
                  value={row.percent === "" || row.percent == null ? "" : row.percent}
                  onChange={(e) => updateTier(index, "percent", e.target.value)}
                  inputProps={{ "aria-label": `Band ${index + 1} percent`, style: { textAlign: "center" } }}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: `${inputRadius}px` },
                    "& .MuiOutlinedInput-input": { fontSize: "0.875rem", py: 1 },
                  }}
                />
                <TextField
                  size="small"
                  disabled={disabled}
                  value={row.from_amount === "" || row.from_amount == null ? "" : row.from_amount}
                  onChange={(e) => updateTier(index, "from_amount", e.target.value)}
                  inputProps={{ "aria-label": `Band ${index + 1} from` }}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: `${inputRadius}px` },
                    "& .MuiOutlinedInput-input": { fontSize: "0.875rem", py: 1 },
                  }}
                />
                <TextField
                  size="small"
                  disabled={disabled}
                  value={row.to_amount === null || row.to_amount === undefined ? "" : row.to_amount}
                  onChange={(e) => updateTier(index, "to_amount", e.target.value)}
                  placeholder="Open"
                  inputProps={{ "aria-label": `Band ${index + 1} to` }}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: `${inputRadius}px` },
                    "& .MuiOutlinedInput-input": { fontSize: "0.875rem", py: 1 },
                  }}
                />
                <IconButton
                  size="small"
                  aria-label={`Remove band ${index + 1}`}
                  disabled={disabled || tiers.length <= 1}
                  onClick={() => removeTier(index)}
                  sx={{
                    color: tiers.length <= 1 ? "action.disabled" : textMuted,
                    "&:hover": { color: "#b91c1c", backgroundColor: "rgba(185, 28, 28, 0.06)" },
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Paper>
          <Button
            size="medium"
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={disabled}
            onClick={addTier}
            sx={{
              mt: 1.5,
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              borderColor: borderSubtle,
              color: textSecondary,
              borderRadius: "10px",
              "&:hover": { borderColor: "#cbd5e1", backgroundColor: "rgba(148, 163, 184, 0.08)" },
            }}
          >
            Add band
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

/**
 * Comparison layout: each plan column has its own editable config.
 * `matrix.activePlanKey` is the plan persisted on the billing rule (one plan per API payload).
 */
function AwsPartnerSupportCompareFields({ disabled, matrix, onMatrixChange, requiredParameters }) {
  const planOpts = useMemo(() => planOptionsFromMeta(requiredParameters), [requiredParameters]);
  const chargeOpts = useMemo(() => chargeSelectionOptionsFromMeta(requiredParameters), [requiredParameters]);

  const columnPlanKeys = useMemo(() => {
    const fromMeta = planOpts
      .map((o) => String(o.value || "").toUpperCase())
      .filter((k) => AWS_PARTNER_SUPPORT_PLAN_PRESETS[k]);
    return fromMeta.length ? fromMeta : Object.keys(AWS_PARTNER_SUPPORT_PLAN_PRESETS);
  }, [planOpts]);

  const planLabelByKey = useMemo(() => {
    const m = {};
    planOpts.forEach((o) => {
      m[String(o.value).toUpperCase()] = o.label;
    });
    return m;
  }, [planOpts]);

  const borderSubtle = "#e2e8f0";
  const borderSoft = "#f1f5f9";
  const surfaceHeader = "#fafbfc";
  const surfaceMuted = "#f8fafc";
  const textPrimary = "#1e293b";
  const textSecondary = "#334155";
  const textMuted = "#64748b";
  const accent = "#2563eb";
  const inputRadius = 2;

  const activeValue = String(matrix.activePlanKey || "BUSINESS_SUPPORT").toUpperCase();

  const patchPlan = (planKey, partial) => {
    if (disabled) return;
    const prev = matrix.byPlan[planKey] || buildAwsPartnerSupportDefaultParameters(planKey);
    const merged = { ...mergeParams(prev, partial), plan_name: planKey };
    onMatrixChange({
      ...matrix,
      byPlan: { ...matrix.byPlan, [planKey]: merged },
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        maxWidth: "100%",
        borderRadius: "12px",
        border: `1px solid ${borderSubtle}`,
        overflow: "hidden",
        backgroundColor: "#ffffff",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
        ...(disabled
          ? {
              opacity: 0.7,
              filter: "grayscale(0.22)",
              pointerEvents: "none",
              transition: "opacity 0.2s ease, filter 0.2s ease",
            }
          : { transition: "opacity 0.2s ease, filter 0.2s ease" }),
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          background: `linear-gradient(180deg, ${surfaceHeader} 0%, #ffffff 100%)`,
          borderBottom: `1px solid ${borderSubtle}`,
        }}
      >
        <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: textPrimary, letterSpacing: "-0.01em" }}>
          Partner support plans
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", color: textMuted, mt: 0.75, lineHeight: 1.6, maxWidth: 720 }}>
          Edit each column independently. Use the radio control in a column to choose which plan this billing rule
          applies when saved. Other columns stay as reference or alternate pricing you can switch to later.
        </Typography>
      </Box>

      <Box sx={{ overflowX: { xs: "auto", sm: "visible" }, width: "100%", minWidth: 0 }}>
        <RadioGroup
          name="aws-partner-support-active-plan"
          value={activeValue}
          onChange={(e) => {
            if (disabled) return;
            const v = String(e.target.value).toUpperCase();
            onMatrixChange({ ...matrix, activePlanKey: v });
          }}
          sx={{
            display: "grid",
            width: "100%",
            minWidth: { xs: columnPlanKeys.length <= 1 ? "100%" : `${columnPlanKeys.length * 280}px`, sm: "100%" },
            gridTemplateColumns: `repeat(${columnPlanKeys.length}, minmax(0, 1fr))`,
          }}
        >
          {columnPlanKeys.map((planKey, colIndex) => {
            const label = planLabelByKey[planKey] || planKey.replace(/_/g, " ");
            const params = matrix.byPlan[planKey] || buildAwsPartnerSupportDefaultParameters(planKey);
            const isActive = activeValue === planKey;
            const preset = AWS_PARTNER_SUPPORT_PLAN_PRESETS[planKey];
            const refMin = preset
              ? formatMinimumSpendReadable(planKey, preset.minimum_charge)
              : null;
            const tiers = normalizeTiersList(params.tiers);
            const mc = params.minimum_charge && typeof params.minimum_charge === "object" ? params.minimum_charge : {};
            const amountStr =
              mc.amount === "" || mc.amount === null || mc.amount === undefined ? "" : String(mc.amount);
            const unitVal = mc.unit || preset?.minimum_charge?.unit || "PER_MONTH";
            const chargeSelection = params.charge_selection || CHARGE_SELECTION_GREATER;
            const showGreaterOfBlock = chargeSelection === CHARGE_SELECTION_GREATER;
            const minimumSpendReadable = formatMinimumSpendReadable(planKey, mc);
            const tierSummaryLines = formatPartnerSupportTierLinesAwsStyle(normalizeTiersList(params.tiers));

            const updateTier = (index, field, rawValue) => {
              const nextTiers = tiers.map((row, i) => {
                if (i !== index) return { ...row };
                if (field === "to_amount") {
                  const s = String(rawValue ?? "").trim();
                  if (s === "") return { ...row, to_amount: null };
                  const n = parseFloat(s.replace(/[^0-9.-]/g, ""));
                  return { ...row, to_amount: Number.isFinite(n) ? n : null };
                }
                if (field === "from_amount") {
                  const raw = String(rawValue).replace(/[^0-9.]/g, "");
                  const parts = raw.split(".");
                  const normalized = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : raw;
                  return { ...row, from_amount: normalized };
                }
                if (field === "percent") {
                  const raw = String(rawValue).replace(/[^0-9.]/g, "");
                  const parts = raw.split(".");
                  const normalized = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}` : raw;
                  return { ...row, percent: normalized };
                }
                return row;
              });
              patchPlan(planKey, { tiers: nextTiers });
            };

            const addTier = () => {
              const last = tiers[tiers.length - 1];
              const from =
                last && last.to_amount != null && last.to_amount !== "" && Number.isFinite(Number(last.to_amount))
                  ? Number(last.to_amount)
                  : last
                    ? Number(last.from_amount) || 0
                    : 0;
              patchPlan(planKey, {
                tiers: [...tiers, { percent: "", from_amount: from, to_amount: null }],
              });
            };

            const removeTier = (index) => {
              if (tiers.length <= 1) return;
              patchPlan(planKey, { tiers: tiers.filter((_, i) => i !== index) });
            };

            return (
              <Box
                key={planKey}
                sx={{
                  borderRight: colIndex < columnPlanKeys.length - 1 ? `1px solid ${borderSubtle}` : "none",
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                  backgroundColor: isActive ? "#f8fafc" : "#ffffff",
                  borderTop: isActive ? `3px solid ${accent}` : "3px solid transparent",
                  transition: "background-color 0.2s ease, border-color 0.2s ease",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    pt: 2,
                    pb: 1.5,
                    borderBottom: `1px solid ${borderSoft}`,
                    backgroundColor: isActive ? "rgba(37, 99, 235, 0.04)" : surfaceHeader,
                  }}
                >
                  <FormControlLabel
                    value={planKey}
                    disabled={disabled}
                    control={
                      <Radio
                        size="small"
                        disableRipple
                        disabled={disabled}
                        sx={{
                          py: 0.25,
                          color: "#94a3b8",
                          "&.Mui-checked": { color: "#2563eb" },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ pl: 0.5 }}>
                        <Typography
                          sx={{
                            fontSize: "0.9375rem",
                            fontWeight: 600,
                            color: textPrimary,
                            lineHeight: 1.35,
                            letterSpacing: "-0.01em",
                          }}
                        >
                          {label}
                        </Typography>
                        <Typography sx={{ fontSize: "0.8125rem", color: textMuted, mt: 0.35, lineHeight: 1.45 }}>
                          {isActive ? "Applied to this billing rule" : "Not selected for this rule"}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      m: 0,
                      alignItems: "flex-start",
                      "& .MuiFormControlLabel-label": { width: "100%" },
                    }}
                  />
                  {refMin && (
                    <Typography
                      sx={{
                        fontSize: "0.8125rem",
                        color: textMuted,
                        mt: 1.25,
                        pl: { xs: 0, sm: 3.25 },
                        lineHeight: 1.5,
                      }}
                    >
                      Catalog default minimum:{" "}
                      <Typography component="span" sx={{ color: textSecondary, fontWeight: 500 }}>
                        {refMin}
                      </Typography>
                    </Typography>
                  )}
                </Box>

                <Box sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                  {chargeOpts.length > 1 && (
                    <FormControl size="small" fullWidth disabled={disabled}>
                      <InputLabel id={`aws-partner-charge-${planKey}`}>Charge calculation</InputLabel>
                      <Select
                        labelId={`aws-partner-charge-${planKey}`}
                        label="Charge calculation"
                        value={chargeSelection}
                        onChange={(e) => patchPlan(planKey, { charge_selection: e.target.value })}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: `${inputRadius}px`,
                            backgroundColor: "#fff",
                            fontSize: "0.875rem",
                          },
                        }}
                      >
                        {chargeOpts.map((o) => (
                          <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.875rem" }}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <Box>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: textSecondary, mb: 1 }}>
                      Minimum charge
                    </Typography>
                    <Typography sx={{ fontSize: "0.875rem", color: textSecondary, lineHeight: 1.5, mb: 1.5 }}>
                      {minimumSpendReadable}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "flex-start" }}>
                      <TextField
                        size="small"
                        label="Amount"
                        disabled={disabled}
                        value={amountStr}
                        onChange={(e) => {
                          const raw = String(e.target.value).replace(/[^0-9.]/g, "");
                          const parts = raw.split(".");
                          const normalized =
                            parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}` : raw;
                          patchPlan(planKey, {
                            minimum_charge: { ...mc, amount: normalized === "" ? "" : normalized, unit: unitVal },
                          });
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        sx={{
                          flex: "1 1 112px",
                          minWidth: 112,
                          "& .MuiOutlinedInput-root": { borderRadius: `${inputRadius}px`, fontSize: "0.875rem" },
                          "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                        }}
                      />
                      <FormControl size="small" sx={{ flex: "1 1 160px", minWidth: 160 }} disabled={disabled}>
                        <InputLabel id={`aws-partner-min-unit-${planKey}`}>Unit</InputLabel>
                        <Select
                          labelId={`aws-partner-min-unit-${planKey}`}
                          label="Unit"
                          value={unitVal}
                          onChange={(e) =>
                            patchPlan(planKey, {
                              minimum_charge: {
                                ...mc,
                                amount:
                                  amountStr === ""
                                    ? AWS_PARTNER_SUPPORT_PLAN_PRESETS[planKey].minimum_charge.amount
                                    : mc.amount,
                                unit: e.target.value,
                              },
                            })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: `${inputRadius}px`,
                              fontSize: "0.875rem",
                            },
                          }}
                        >
                          <MenuItem value="PER_ACCOUNT_PER_MONTH" sx={{ fontSize: "0.875rem" }}>
                            Per account / month
                          </MenuItem>
                          <MenuItem value="PER_MONTH" sx={{ fontSize: "0.875rem" }}>
                            Per month
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: textSecondary, mb: 0.75 }}>
                      {showGreaterOfBlock ? "Pricing preview" : "Tier structure"}
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.25, py: 0 }}>
                      {tierSummaryLines.map((line, i) => (
                        <Typography
                          key={i}
                          component="li"
                          sx={{
                            fontSize: "0.8125rem",
                            color: textMuted,
                            lineHeight: 1.65,
                            mb: i === tierSummaryLines.length - 1 ? 0 : 0.5,
                          }}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: textSecondary, mb: 1 }}>
                      Usage bands
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: "10px",
                        overflow: "hidden",
                        width: "100%",
                        borderColor: borderSubtle,
                        boxShadow: "none",
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "48px minmax(0,1fr) minmax(0,1fr) 40px",
                          gap: 1,
                          alignItems: "center",
                          px: 1.5,
                          py: 1,
                          backgroundColor: surfaceMuted,
                          borderBottom: `1px solid ${borderSubtle}`,
                        }}
                      >
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: textMuted }}>%</Typography>
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: textMuted }}>
                          From ($)
                        </Typography>
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: textMuted }}>
                          To ($)
                        </Typography>
                        <Box aria-hidden />
                      </Box>
                      {tiers.map((row, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "48px minmax(0,1fr) minmax(0,1fr) 40px",
                            gap: 1,
                            alignItems: "center",
                            px: 1.5,
                            py: 1,
                            borderTop: index === 0 ? "none" : `1px solid ${borderSubtle}`,
                            backgroundColor: "#fff",
                          }}
                        >
                          <TextField
                            size="small"
                            disabled={disabled}
                            value={row.percent === "" || row.percent == null ? "" : row.percent}
                            onChange={(e) => updateTier(index, "percent", e.target.value)}
                            inputProps={{
                              "aria-label": `${label} band ${index + 1} percent`,
                              style: { textAlign: "center" },
                            }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: `${inputRadius}px` }, "& .MuiOutlinedInput-input": { fontSize: "0.875rem", py: 1 } }}
                          />
                          <TextField
                            size="small"
                            disabled={disabled}
                            value={row.from_amount === "" || row.from_amount == null ? "" : row.from_amount}
                            onChange={(e) => updateTier(index, "from_amount", e.target.value)}
                            inputProps={{ "aria-label": `${label} band ${index + 1} from` }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: `${inputRadius}px` }, "& .MuiOutlinedInput-input": { fontSize: "0.875rem", py: 1 } }}
                          />
                          <TextField
                            size="small"
                            disabled={disabled}
                            value={row.to_amount === null || row.to_amount === undefined ? "" : row.to_amount}
                            onChange={(e) => updateTier(index, "to_amount", e.target.value)}
                            placeholder="Open"
                            inputProps={{ "aria-label": `${label} band ${index + 1} to` }}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: `${inputRadius}px` }, "& .MuiOutlinedInput-input": { fontSize: "0.875rem", py: 1 } }}
                          />
                          <IconButton
                            size="small"
                            aria-label={`Remove band ${index + 1}`}
                            disabled={disabled || tiers.length <= 1}
                            onClick={() => removeTier(index)}
                            sx={{
                              color: tiers.length <= 1 ? "action.disabled" : textMuted,
                              "&:hover": { color: "#b91c1c", backgroundColor: "rgba(185, 28, 28, 0.06)" },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Paper>
                    <Button
                      size="medium"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      disabled={disabled}
                      onClick={addTier}
                      sx={{
                        mt: 1.5,
                        alignSelf: "flex-start",
                        textTransform: "none",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        px: 1.5,
                        borderColor: borderSubtle,
                        color: textSecondary,
                        borderRadius: "10px",
                        "&:hover": {
                          borderColor: "#cbd5e1",
                          backgroundColor: "rgba(148, 163, 184, 0.08)",
                        },
                      }}
                    >
                      Add band
                    </Button>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </RadioGroup>
      </Box>
    </Paper>
  );
}

export default function AwsPartnerSupportRuleFields(props) {
  if (props.variant === "single") {
    return (
      <AwsPartnerSupportSinglePlanFields
        disabled={props.disabled}
        parameters={props.parameters || {}}
        onChange={props.onChange}
        requiredParameters={props.requiredParameters || []}
      />
    );
  }
  return (
    <AwsPartnerSupportCompareFields
      disabled={props.disabled}
      matrix={props.matrix}
      onMatrixChange={props.onMatrixChange}
      requiredParameters={props.requiredParameters || []}
    />
  );
}
